import { SyntheticEvent, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { KTIcon } from '../../../../../_metronic/helpers'
import { useFormik } from 'formik'
import { DEFAULT_NEW_EMPLOYEE_OBJ, NEW_EMPLOYEE_SCHEMA, ROLES_FOR_CASHBOX, ROLES_FOR_MERCHANT, ROLES_FOR_NEW_EMPLOYEE, ROLES_WITH_SINGLE_MERCHANT } from './consts'
import { generatePassword } from '../../../../common/core/password'
import { INewEmployee } from './newEmployee.types'
import { ROLE_NAME } from '../../../../../constants'
import { ROLE, useAuth } from '../../../auth'
import { IsGranted } from '../../../auth/PrivateRoute'
import { addNewEmployee, getMerchantsForSelect, IMerchantOption } from '../../api'
import { getListCashBoxesForSelect } from '../../../../common/core/_requests'
import { SelectOption } from '../../../../common/filter/models'
import { ICashBox } from '../../../../../types'
import ReactSelect from 'react-select'
import { SelectStyles } from '../../../../../components/Select'
import { useThemeMode } from '../../../../../_metronic/partials'

const AppSwal = withReactContent(Swal)

interface NewEmployeeProps {
  callback: () => void
  presetCashBoxId?: number
  presetCashBoxTitle?: string
  allowedRoles?: string[]
  buttonText?: string
  modalTitle?: string
  buttonClassName?: string
  showIcon?: boolean
}

export const NewEmployee = ({ 
  callback, 
  presetCashBoxId, 
  presetCashBoxTitle, 
  allowedRoles, 
  buttonText = 'Добавить нового сотрудника',
  modalTitle = 'Добавить нового сотрудника',
  buttonClassName = 'btn btn-success',
  showIcon = false
}: NewEmployeeProps) => {
  const { mode: theme } = useThemeMode()
  const { currentUser } = useAuth()
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false)
  const [cashboxes, setCashboxes] = useState<Array<SelectOption>>([])
  const [loadingCashboxes, setLoadingCashboxes] = useState(false)
  const [merchants, setMerchants] = useState<Array<IMerchantOption>>([])
  const [loadingMerchants, setLoadingMerchants] = useState(false)
  const [loading, setLoading] = useState(false)

  const initialValues: INewEmployee = { 
    ...DEFAULT_NEW_EMPLOYEE_OBJ,
    ...(presetCashBoxId && { cashboxId: presetCashBoxId })
  }
  const formik = useFormik<INewEmployee>({
    initialValues,
    validationSchema: NEW_EMPLOYEE_SCHEMA,
    onSubmit: (values) => {
      addEmployee(values)
    },
    enableReinitialize: true,
  })

  const fetchCashboxes = async () => {
    setLoadingCashboxes(true)

    try {
      const response = await getListCashBoxesForSelect()

      if (!response.data) {
        return false
      }

      if (response.data.success && response.data.cashBoxes) {
        const cashBoxes: Array<SelectOption> = []

        response.data.cashBoxes.forEach((value: ICashBox) => {
          const item = {
            value: value.id,
            label: value.title,
          }

          cashBoxes.push(item)
        })

        setCashboxes(cashBoxes)
        setLoadingCashboxes(false)
      }
    } catch (err) {
      console.log('error catched: ', err)
    }
  }

  const fetchMerchants = async () => {
    setLoadingMerchants(true)

    try {
      const response = await getMerchantsForSelect()

      if (response.data?.success && response.data.merchants) {
        setMerchants(response.data.merchants)
      }
    } catch (err) {
      console.log('error catched: ', err)
    } finally {
      setLoadingMerchants(false)
    }
  }

  useEffect(() => {
    if (ROLES_FOR_CASHBOX.includes(formik.values.role as any)) {
      if (!presetCashBoxId && !cashboxes.length) {
        fetchCashboxes()
      }
    }

    if (ROLES_FOR_MERCHANT.includes(formik.values.role as any)) {
      if (!merchants.length) {
        fetchMerchants()
      }
    }

    // Clear merchant fields when role changes
    if (!ROLES_FOR_MERCHANT.includes(formik.values.role as any)) {
      formik.setFieldValue('merchantId', undefined)
      formik.setFieldValue('merchantIds', [])
    }
  }, [formik.values.role, presetCashBoxId])

  const onClickHandler = () => {
    setShowAddEmployeeModal(true)
  }

  const onCancelHandler = (e: SyntheticEvent) => {
    formik.handleReset(e)
    setShowAddEmployeeModal(false)
  }

  const addEmployee = async ({ uuid, password, role, cashboxId, merchantId, merchantIds }: any) => {
    setLoading(true)

    try {
      const actualCashboxId = presetCashBoxId || cashboxId
      const response = await addNewEmployee(uuid, password, role, actualCashboxId, merchantId, merchantIds)

      if (!response.data) {
        setLoading(false)
        return false
      }

      if (response.data.success && response.data) {
        formik.resetForm()
        setShowAddEmployeeModal(false)
        const successMessage = presetCashBoxTitle 
          ? `Сотрудник '${uuid}' успешно добавлен в кассу '${presetCashBoxTitle}'!`
          : `Новый сотрудник '${uuid}' успешно добавлен!`
        
        AppSwal.fire({
          title: successMessage,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          willClose: () => {
            callback()
          },
        })
      }

      setLoading(false)
    } catch (err) {
      console.log('error catched: ', err)
      setLoading(false)
    }
  }

  return (
    <>
      <button className={buttonClassName} onClick={onClickHandler}>
        {showIcon && <KTIcon iconName="plus" className="fs-2" />}
        {buttonText}
      </button>

      <Modal
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered mw-800px'
        show={showAddEmployeeModal}
        backdrop={true}
      >
        <div className='modal-header'>
          <h2>{modalTitle}</h2>
          <div
            className='btn btn-sm btn-icon btn-active-color-primary'
            onClick={() => setShowAddEmployeeModal(false)}
          >
            <KTIcon className='fs-1' iconName='cross' />
          </div>
        </div>

        <div className='modal-body py-lg-10 px-lg-10'>
          <form onSubmit={formik.handleSubmit} noValidate className='form'>
            <div className='card-body p-9'>
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>Логин</label>

                <div className='col-lg-8 fv-row'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Логин'
                    {...formik.getFieldProps('uuid')}
                  />
                  {formik.touched.uuid && formik.errors.uuid && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.uuid}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>Пароль</label>

                <div className='col-lg-8 fv-row'>
                  <div className='input-group input-group-lg'>
                    <input
                      type='text'
                      autoComplete='off'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Пароль'
                      {...formik.getFieldProps('password')}
                    />
                    <button
                      type='button'
                      className='btn btn-light-primary'
                      title='Сгенерировать пароль'
                      onClick={() => formik.setFieldValue('password', generatePassword())}
                    >
                      Сгенерировать
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.password}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>Роль</label>

                <div className='col-lg-8 fv-row'>
                  <select
                    className='form-select form-select-solid form-select-lg fw-bold'
                    {...formik.getFieldProps('role')}
                  >
                    <option value={''}>{''}</option>
                    {(allowedRoles || ROLES_FOR_NEW_EMPLOYEE)
                      .filter(
                        (value) =>
                          value !== ROLE.ROLE_MERCHANT_MANAGER ||
                          IsGranted([ROLE.ROLE_ADMIN], currentUser)
                      )
                      .map((value, index) => (
                      <option value={value} key={index}>
                        {ROLE_NAME[value]}
                      </option>
                    ))}
                  </select>
                  {formik.touched.role && formik.errors.role && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.role}</div>
                    </div>
                  )}
                </div>
              </div>

              {['ROLE_CASHIER', 'ROLE_SENIOR_CASHIER'].includes(
                formik.getFieldProps('role').value
              ) && !presetCashBoxId && (
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label required fw-bold fs-6'>Касса</label>

                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-solid form-select-lg fw-bold'
                      {...formik.getFieldProps('cashboxId')}
                    >
                      <option value={''}>{loadingCashboxes ? 'Загрузка касс' : ''}</option>
                      {cashboxes.map((cashbox, index) => (
                        <option value={cashbox.value} key={index}>
                          {cashbox.label}
                        </option>
                      ))}
                    </select>
                    {formik.touched.cashboxId && formik.errors.cashboxId && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.cashboxId}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {['ROLE_CASHIER', 'ROLE_SENIOR_CASHIER'].includes(
                formik.getFieldProps('role').value
              ) && presetCashBoxId && presetCashBoxTitle && (
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Касса</label>
                  <div className='col-lg-8'>
                    <div className='form-control form-control-lg form-control-solid bg-light'>
                      {presetCashBoxTitle}
                    </div>
                  </div>
                </div>
              )}

              {ROLES_WITH_SINGLE_MERCHANT.includes(formik.getFieldProps('role').value as any) && (
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label required fw-bold fs-6'>Мерчант</label>

                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-solid form-select-lg fw-bold'
                      {...formik.getFieldProps('merchantId')}
                    >
                      <option value={''}>{loadingMerchants ? 'Загрузка мерчантов' : ''}</option>
                      {merchants.map((merchant) => (
                        <option value={merchant.id} key={merchant.id}>
                          {merchant.title}
                        </option>
                      ))}
                    </select>
                    {formik.touched.merchantId && formik.errors.merchantId && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.merchantId}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(formik.getFieldProps('role').value === ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR ||
                formik.getFieldProps('role').value === ROLE.ROLE_MERCHANT_MANAGER) && (
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label required fw-bold fs-6'>Мерчанты</label>

                  <div className='col-lg-8 fv-row'>
                    {loadingMerchants ? (
                      <div className='form-control form-control-lg form-control-solid bg-light'>Загрузка мерчантов...</div>
                    ) : (
                      <ReactSelect
                        isMulti
                        options={merchants.map((m) => ({ value: m.id, label: m.title }))}
                        value={merchants
                          .filter((m) => (formik.values.merchantIds || []).includes(m.id))
                          .map((m) => ({ value: m.id, label: m.title }))}
                        onChange={(selected) => {
                          formik.setFieldValue(
                            'merchantIds',
                            selected ? selected.map((item: any) => item.value) : []
                          )
                        }}
                        placeholder='Выберите мерчантов'
                        isClearable
                        styles={SelectStyles(theme)}
                      />
                    )}
                    {formik.touched.merchantIds && formik.errors.merchantIds && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{String(formik.errors.merchantIds)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className='card-footer d-flex justify-content-end py-6 px-9'>
              <button type='reset' className='btn' onClick={onCancelHandler}>
                Отмена
              </button>
              <button type='submit' className='btn btn-primary' disabled={loading}>
                {!loading && 'Добавить сотрудника'}
                {loading && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Идёт добавление сотрудника...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  )
}
